import { create } from 'zustand';

// 회사별로 그룹 선택을 분리 저장한다 — 단일 키를 쓰면 A사에서 고른 그룹 id가 B사로 넘어가
// 5개 메뉴가 GROUP_ACCESS_DENIED(403)로 막히고 스스로 회복하지 못한다.
const STORAGE_PREFIX = 'safetyhub:currentGroupId';

function storageKey(companyId: string): string {
  return `${STORAGE_PREFIX}:${companyId}`;
}

function loadForCompany(companyId: string | null): string | null {
  if (!companyId) return null;
  try {
    return localStorage.getItem(storageKey(companyId)) || null;
  } catch {
    return null;
  }
}

// store 초기화 시점엔 아직 React(auth store)가 없다 — persist된 auth 상태에서 활성 회사를
// 직접 읽어 그 회사의 그룹으로 동기 하이드레이션한다. 이렇게 하면 첫 렌더에서 currentGroupId가
// null→저장값으로 사후 변경되지 않아(회사 미전환 시 syncCompany가 no-op), 목록 페이지의
// "그룹 변경 시 현장 필터 초기화"가 URL 딥링크를 지우는 오작동을 막는다.
// (auth persist 키 'auth-storage' + partialize의 currentCompanyId 형상에 의존)
function loadInitialCompanyId(): string | null {
  try {
    const raw = localStorage.getItem('auth-storage');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state?: { currentCompanyId?: string | null } };
    return parsed?.state?.currentCompanyId ?? null;
  } catch {
    return null;
  }
}

interface GroupStore {
  /** 지금 필터를 소유한 회사 — 이 값이 바뀌면 그 회사에 저장된 선택으로 교체된다. */
  currentCompanyId: string | null;
  currentGroupId: string | null;
  setCurrentGroupId: (id: string | null) => void;
  /**
   * 활성 회사를 알린다(회사 전환 감지). 회사가 실제로 바뀐 경우에만 그 회사에 저장된
   * 그룹 선택을 불러와 교체한다 — 저장된 값이 없으면 null(필터 없음)로 시작한다.
   */
  syncCompany: (companyId: string | null) => void;
}

const initialCompanyId = loadInitialCompanyId();

export const useGroupStore = create<GroupStore>()((set, get) => ({
  currentCompanyId: initialCompanyId,
  currentGroupId: loadForCompany(initialCompanyId),

  setCurrentGroupId: (id) => {
    const { currentCompanyId } = get();
    try {
      if (currentCompanyId) {
        if (id) localStorage.setItem(storageKey(currentCompanyId), id);
        else localStorage.removeItem(storageKey(currentCompanyId));
      }
    } catch {
      // localStorage 접근 불가 시 무시
    }
    set({ currentGroupId: id });
  },

  syncCompany: (companyId) => {
    if (companyId === get().currentCompanyId) return;
    set({ currentCompanyId: companyId, currentGroupId: loadForCompany(companyId) });
  },
}));
